/**
 * Every supported chain and asset lives in this one file (spec §2).
 * To add a token: add one entry to ASSET_DEFINITIONS. Nothing else should need to change.
 *
 * Amounts (minDeposit, minWithdrawal, withdrawalFeeEstimate) are written as human strings
 * and converted once to base-unit BigInts by getAssets().
 *
 * Contract addresses marked `null` with `enabled: false` on testnet have no official test
 * token — a mock token will be deployed in Phase 3 and its address recorded here.
 */
import { parseUnits } from "./money.js";
export const CHAIN_KEYS = ["ethereum", "base", "arbitrum", "bsc", "tron", "bitcoin", "solana"];
const evmExplorer = (base) => ({
    explorerTx: `${base}/tx/{hash}`,
    explorerAddress: `${base}/address/{address}`,
});
export const CHAINS = {
    ethereum: {
        key: "ethereum",
        family: "evm",
        displayName: "Ethereum",
        nativeSymbol: "ETH",
        networks: {
            mainnet: {
                name: "Ethereum Mainnet",
                evmChainId: 1,
                ...evmExplorer("https://etherscan.io"),
                rpcEnvVar: "RPC_ETHEREUM_MAINNET",
                defaultRpcUrl: "https://ethereum-rpc.publicnode.com",
            },
            testnet: {
                name: "Sepolia",
                evmChainId: 11155111,
                ...evmExplorer("https://sepolia.etherscan.io"),
                rpcEnvVar: "RPC_ETHEREUM_TESTNET",
                defaultRpcUrl: "https://ethereum-sepolia-rpc.publicnode.com",
            },
        },
    },
    base: {
        key: "base",
        family: "evm",
        displayName: "Base",
        nativeSymbol: "ETH",
        networks: {
            mainnet: {
                name: "Base",
                evmChainId: 8453,
                ...evmExplorer("https://basescan.org"),
                rpcEnvVar: "RPC_BASE_MAINNET",
                defaultRpcUrl: "https://mainnet.base.org",
            },
            testnet: {
                name: "Base Sepolia",
                evmChainId: 84532,
                ...evmExplorer("https://sepolia.basescan.org"),
                rpcEnvVar: "RPC_BASE_TESTNET",
                defaultRpcUrl: "https://sepolia.base.org",
            },
        },
    },
    arbitrum: {
        key: "arbitrum",
        family: "evm",
        displayName: "Arbitrum One",
        nativeSymbol: "ETH",
        networks: {
            mainnet: {
                name: "Arbitrum One",
                evmChainId: 42161,
                ...evmExplorer("https://arbiscan.io"),
                rpcEnvVar: "RPC_ARBITRUM_MAINNET",
                defaultRpcUrl: "https://arb1.arbitrum.io/rpc",
            },
            testnet: {
                name: "Arbitrum Sepolia",
                evmChainId: 421614,
                ...evmExplorer("https://sepolia.arbiscan.io"),
                rpcEnvVar: "RPC_ARBITRUM_TESTNET",
                defaultRpcUrl: "https://sepolia-rollup.arbitrum.io/rpc",
            },
        },
    },
    bsc: {
        key: "bsc",
        family: "evm",
        displayName: "BNB Smart Chain",
        nativeSymbol: "BNB",
        networks: {
            mainnet: {
                name: "BNB Smart Chain",
                evmChainId: 56,
                ...evmExplorer("https://bscscan.com"),
                rpcEnvVar: "RPC_BSC_MAINNET",
                defaultRpcUrl: "https://bsc-dataseed.bnbchain.org",
            },
            testnet: {
                name: "BSC Testnet",
                evmChainId: 97,
                ...evmExplorer("https://testnet.bscscan.com"),
                rpcEnvVar: "RPC_BSC_TESTNET",
                defaultRpcUrl: "https://bsc-testnet-rpc.publicnode.com",
            },
        },
    },
    tron: {
        key: "tron",
        family: "tron",
        displayName: "Tron",
        nativeSymbol: "TRX",
        networks: {
            mainnet: {
                name: "Tron Mainnet",
                explorerTx: "https://tronscan.org/#/transaction/{hash}",
                explorerAddress: "https://tronscan.org/#/address/{address}",
                rpcEnvVar: "RPC_TRON_MAINNET",
                defaultRpcUrl: "https://api.trongrid.io",
            },
            testnet: {
                name: "Nile",
                explorerTx: "https://nile.tronscan.org/#/transaction/{hash}",
                explorerAddress: "https://nile.tronscan.org/#/address/{address}",
                rpcEnvVar: "RPC_TRON_TESTNET",
                defaultRpcUrl: "https://nile.trongrid.io",
            },
        },
    },
    bitcoin: {
        key: "bitcoin",
        family: "bitcoin",
        displayName: "Bitcoin",
        nativeSymbol: "BTC",
        networks: {
            mainnet: {
                name: "Bitcoin Mainnet",
                explorerTx: "https://mempool.space/tx/{hash}",
                explorerAddress: "https://mempool.space/address/{address}",
                rpcEnvVar: "RPC_BITCOIN_MAINNET",
                defaultRpcUrl: "https://mempool.space/api",
            },
            testnet: {
                name: "Signet",
                explorerTx: "https://mempool.space/signet/tx/{hash}",
                explorerAddress: "https://mempool.space/signet/address/{address}",
                rpcEnvVar: "RPC_BITCOIN_TESTNET",
                defaultRpcUrl: "https://mempool.space/signet/api",
            },
        },
    },
    solana: {
        key: "solana",
        family: "solana",
        displayName: "Solana",
        nativeSymbol: "SOL",
        networks: {
            mainnet: {
                name: "Solana Mainnet Beta",
                explorerTx: "https://explorer.solana.com/tx/{hash}",
                explorerAddress: "https://explorer.solana.com/address/{address}",
                rpcEnvVar: "RPC_SOLANA_MAINNET",
                defaultRpcUrl: "https://api.mainnet-beta.solana.com",
            },
            testnet: {
                name: "Devnet",
                explorerTx: "https://explorer.solana.com/tx/{hash}?cluster=devnet",
                explorerAddress: "https://explorer.solana.com/address/{address}?cluster=devnet",
                rpcEnvVar: "RPC_SOLANA_TESTNET",
                defaultRpcUrl: "https://api.devnet.solana.com",
            },
        },
    },
};
const both = (value) => ({ testnet: value, mainnet: value });
/**
 * Confirmation counts are starting values — editable here. Solana "1" means
 * the "finalized" commitment level; Tron 19 blocks = solidified.
 */
const ASSET_DEFINITIONS = [
    // ── Ethereum ──
    {
        chain: "ethereum",
        symbol: "ETH",
        name: "Ether",
        decimals: 18,
        contractAddress: both(null),
        minConfirmations: { testnet: 3, mainnet: 12 },
        minDeposit: "0.001",
        minWithdrawal: "0.002",
        withdrawalFeeEstimate: "0.0005",
        enabled: both(true),
    },
    {
        chain: "ethereum",
        symbol: "USDT",
        name: "Tether USD",
        decimals: 6,
        contractAddress: { testnet: null, mainnet: "0xdAC17F958D2ee523a2206206994597C13D831ec7" },
        minConfirmations: { testnet: 3, mainnet: 12 },
        minDeposit: "1",
        minWithdrawal: "5",
        withdrawalFeeEstimate: "3",
        enabled: { testnet: false, mainnet: true },
    },
    {
        chain: "ethereum",
        symbol: "USDC",
        name: "USD Coin",
        decimals: 6,
        // Testnet: Circle's official Sepolia USDC.
        contractAddress: {
            testnet: "0x1c7D4B196Cb0C7B01d743Fbc6116a902379C7238",
            mainnet: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48",
        },
        minConfirmations: { testnet: 3, mainnet: 12 },
        minDeposit: "1",
        minWithdrawal: "5",
        withdrawalFeeEstimate: "3",
        enabled: both(true),
    },
    // ── Base ──
    {
        chain: "base",
        symbol: "ETH",
        name: "Ether",
        decimals: 18,
        contractAddress: both(null),
        minConfirmations: { testnet: 5, mainnet: 20 },
        minDeposit: "0.0001",
        minWithdrawal: "0.0005",
        withdrawalFeeEstimate: "0.00002",
        enabled: both(true),
    },
    {
        chain: "base",
        symbol: "USDC",
        name: "USD Coin",
        decimals: 6,
        // Testnet: Circle's official Base Sepolia USDC.
        contractAddress: {
            testnet: "0x036CbD53842c5426634e7929541eC2318f3dCF7e",
            mainnet: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913",
        },
        minConfirmations: { testnet: 5, mainnet: 20 },
        minDeposit: "0.5",
        minWithdrawal: "1",
        withdrawalFeeEstimate: "0.1",
        enabled: both(true),
    },
    // ── Arbitrum ──
    {
        chain: "arbitrum",
        symbol: "ETH",
        name: "Ether",
        decimals: 18,
        contractAddress: both(null),
        minConfirmations: { testnet: 5, mainnet: 20 },
        minDeposit: "0.0001",
        minWithdrawal: "0.0005",
        withdrawalFeeEstimate: "0.00002",
        enabled: both(true),
    },
    {
        chain: "arbitrum",
        symbol: "USDT",
        name: "Tether USD",
        decimals: 6,
        contractAddress: { testnet: null, mainnet: "0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9" },
        minConfirmations: { testnet: 5, mainnet: 20 },
        minDeposit: "0.5",
        minWithdrawal: "1",
        withdrawalFeeEstimate: "0.1",
        enabled: { testnet: false, mainnet: true },
    },
    {
        chain: "arbitrum",
        symbol: "USDC",
        name: "USD Coin",
        decimals: 6,
        // Testnet: Circle's official Arbitrum Sepolia USDC.
        contractAddress: {
            testnet: "0x75faf114eafb1BDbe2F0316DF893fd58CE46AA4d",
            mainnet: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831",
        },
        minConfirmations: { testnet: 5, mainnet: 20 },
        minDeposit: "0.5",
        minWithdrawal: "1",
        withdrawalFeeEstimate: "0.1",
        enabled: both(true),
    },
    // ── BNB Smart Chain (BSC stablecoins use 18 decimals; mocks must match) ──
    {
        chain: "bsc",
        symbol: "BNB",
        name: "BNB",
        decimals: 18,
        contractAddress: both(null),
        minConfirmations: { testnet: 5, mainnet: 15 },
        minDeposit: "0.001",
        minWithdrawal: "0.002",
        withdrawalFeeEstimate: "0.0002",
        enabled: both(true),
    },
    {
        chain: "bsc",
        symbol: "USDT",
        name: "Tether USD",
        decimals: 18,
        contractAddress: { testnet: null, mainnet: "0x55d398326f99059fF775485246999027B3197955" },
        minConfirmations: { testnet: 5, mainnet: 15 },
        minDeposit: "0.5",
        minWithdrawal: "1",
        withdrawalFeeEstimate: "0.1",
        enabled: { testnet: false, mainnet: true },
    },
    {
        chain: "bsc",
        symbol: "USDC",
        name: "USD Coin",
        decimals: 18,
        contractAddress: { testnet: null, mainnet: "0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d" },
        minConfirmations: { testnet: 5, mainnet: 15 },
        minDeposit: "0.5",
        minWithdrawal: "1",
        withdrawalFeeEstimate: "0.1",
        enabled: { testnet: false, mainnet: true },
    },
    // ── Tron ──
    {
        chain: "tron",
        symbol: "TRX",
        name: "Tronix",
        decimals: 6,
        contractAddress: both(null),
        minConfirmations: both(19),
        minDeposit: "1",
        minWithdrawal: "5",
        withdrawalFeeEstimate: "1",
        enabled: both(true),
    },
    {
        chain: "tron",
        symbol: "USDT",
        name: "Tether USD (TRC-20)",
        decimals: 6,
        // Testnet: Nile USDT — verified on chain in Phase 3b (symbol USDT, "Tether USD", 6 decimals).
        contractAddress: {
            testnet: "TXYZopYRdj2D9XRtbG411XZZ3kM5VkAeBf",
            mainnet: "TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t",
        },
        minConfirmations: both(19),
        minDeposit: "1",
        minWithdrawal: "5",
        withdrawalFeeEstimate: "2",
        enabled: both(true),
    },
    // ── Bitcoin ──
    {
        chain: "bitcoin",
        symbol: "BTC",
        name: "Bitcoin",
        decimals: 8,
        contractAddress: both(null),
        minConfirmations: { testnet: 1, mainnet: 3 },
        minDeposit: "0.00005",
        minWithdrawal: "0.0001",
        withdrawalFeeEstimate: "0.00002",
        enabled: both(true),
    },
    // ── Solana ──
    {
        chain: "solana",
        symbol: "SOL",
        name: "Solana",
        decimals: 9,
        contractAddress: both(null),
        minConfirmations: both(1),
        minDeposit: "0.001",
        minWithdrawal: "0.01",
        withdrawalFeeEstimate: "0.00001",
        enabled: both(true),
    },
    {
        chain: "solana",
        symbol: "USDC",
        name: "USD Coin",
        decimals: 6,
        // Testnet: Circle's official devnet USDC mint.
        contractAddress: {
            testnet: "4zMMC9srt5Ri5X14GAgXhaHii3GnPAEERYPJgZJDncDU",
            mainnet: "EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v",
        },
        minConfirmations: both(1),
        minDeposit: "0.5",
        minWithdrawal: "1",
        withdrawalFeeEstimate: "0.1",
        enabled: both(true),
    },
    {
        chain: "solana",
        symbol: "USDT",
        name: "Tether USD",
        decimals: 6,
        contractAddress: { testnet: null, mainnet: "Es9vMFrzaCERmJfrF4H2FYD4KCoNkY11McCe8BenwNYB" },
        minConfirmations: both(1),
        minDeposit: "0.5",
        minWithdrawal: "1",
        withdrawalFeeEstimate: "0.1",
        enabled: { testnet: false, mainnet: true },
    },
];
/** All assets resolved for one network mode, including disabled ones. */
export function getAssets(mode) {
    return ASSET_DEFINITIONS.map((def) => ({
        chain: def.chain,
        symbol: def.symbol,
        name: def.name,
        contractAddress: def.contractAddress[mode],
        decimals: def.decimals,
        minConfirmations: def.minConfirmations[mode],
        minDeposit: parseUnits(def.minDeposit, def.decimals),
        minWithdrawal: parseUnits(def.minWithdrawal, def.decimals),
        withdrawalFeeEstimate: parseUnits(def.withdrawalFeeEstimate, def.decimals),
        enabled: def.enabled[mode],
    }));
}
export function getEnabledAssets(mode) {
    return getAssets(mode).filter((a) => a.enabled);
}
export function getAsset(mode, chain, symbol) {
    const asset = getAssets(mode).find((a) => a.chain === chain && a.symbol === symbol);
    if (!asset)
        throw new Error(`Unknown asset ${symbol} on ${chain}`);
    return asset;
}
export function getNetwork(mode, chain) {
    return CHAINS[chain].networks[mode];
}
/** RPC URL for a chain: the .env override if set, otherwise the public default. */
export function getRpcUrl(mode, chain, env) {
    const network = getNetwork(mode, chain);
    return env[network.rpcEnvVar]?.trim() || network.defaultRpcUrl;
}
export function explorerTxUrl(mode, chain, hash) {
    return getNetwork(mode, chain).explorerTx.replace("{hash}", encodeURIComponent(hash));
}
export function explorerAddressUrl(mode, chain, address) {
    return getNetwork(mode, chain).explorerAddress.replace("{address}", encodeURIComponent(address));
}
//# sourceMappingURL=chains.js.map